import { api } from './authenticatedApi';

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  platform: string;
  sourceUrl: string | null;
  fetchedAt: string | null;
  settings: { deliveryMode?: string; autonomyLevel?: string } | null;
  repoCount: number;
  analyzedCount: number;
  meanScore: number | null;
  maintainedRepos: number;
  providers: { platform: string; count: number }[];
  automatableHoursSaved: number;
  recurringHoursPerYear: number;
  scoreBuckets: { label: string; count: number }[];
  totalPlanItems: number;
  automatableItems: number;
  monorepoCount: number;
}

export interface OrgRepoRow {
  id: string;
  name: string;
  latestAnalysisScore: number | null;
  latestAnalysisAt: string | null;
  analysisStatus: string | null;
  maintenancePlan: { totalItems: number; automatableCount: number } | null;
}

export interface OrgDetail extends OrgSummary {
  repositories: OrgRepoRow[];
}

export interface FleetTopline {
  orgCount: number;
  repoCount: number;
  analyzedCount: number;
  meanScore: number | null;
  maintainedRepos: number;
  scoreBuckets: { label: string; count: number }[];
}

export class OrganizationService {
  static async list() {
    return api.get<{ topline: FleetTopline; organizations: OrgSummary[] }>('/orgs');
  }

  static async create(data: { name: string; slug?: string; platform?: string }) {
    return api.post<OrgSummary>('/orgs', data);
  }

  static async listAttachable(id: string) {
    return api.get<{
      repositories: Array<{
        id: string;
        name: string;
        fullName: string;
        platform: string;
        latestAnalysisScore: number | null;
      }>;
    }>(`/orgs/${id}/attachable-repositories`);
  }

  static async attachRepositories(id: string, repositoryIds: string[]) {
    return api.post<{ attached: number }>(`/orgs/${id}/repositories`, { repositoryIds });
  }

  static async detachRepositories(id: string, repositoryIds: string[]) {
    return api.delete<{ detached: number }>(`/orgs/${id}/repositories`, {
      data: { repositoryIds },
    });
  }

  static async get(id: string) {
    return api.get<OrgDetail>(`/orgs/${id}`);
  }

  static async updateSettings(id: string, settings: object) {
    return api.patch<OrgSummary>(`/orgs/${id}/settings`, settings);
  }

  static async delete(id: string) {
    return api.delete<{ deleted: boolean; detachedRepos: number }>(`/orgs/${id}`);
  }

  static async importFromPlatform(id: string, platform: string, slug: string) {
    return api.post<{ fetched: number; created: number; updated: number }>(`/orgs/${id}/import`, {
      platform,
      slug,
    });
  }
}
